"use client";

import Link from "next/link";
import { ArrowRight, Check } from "lucide-react";

import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import { whatsappMessage } from "@/lib/whatsapp";
import { WhatsAppCta } from "@/components/shared/WhatsAppCta";

// OpenAI partner CTA for ChatGPT blog posts (Jazz, 2026-10-08: every article needs a CTA
// after the intro and a closing CTA). Mirrors SonarPartnerCTA: "inline" sits after the
// intro, "band" closes the post. Approved wording is exactly "OpenAI Select Partner"
// and stays text-only: never put the OpenAI badge or logo here, and never link to
// OpenAI's partner listing pages (Select partners are not listed there).
const PARTNER_PAGE = "/partners/openai-partner/";

interface ChatGPTPartnerCTAProps {
  /** Page name for the WhatsApp message, e.g. "your OpenAI dots article". */
  source: string;
  /** GA4 "Click location" prefix, e.g. "openai_dots_blog". */
  location: string;
  variant?: "band" | "inline";
  /** Optional topic-specific heading for the band (English; translated via t). */
  title?: string;
}

const POINTS = [
  "ChatGPT rollout for your team in Malaysia or Singapore",
  "Agents set up with approval rules and a review routine",
  "Team training, HRD Corp claimable in Malaysia",
];

export default function ChatGPTPartnerCTA({
  source,
  location,
  variant = "band",
  title = "Roll out ChatGPT and agents with a local partner",
}: ChatGPTPartnerCTAProps) {
  const locale = useLocale();
  const partnerHref = getLocalizedPathname(PARTNER_PAGE, locale);
  const wa = whatsappMessage("I'd like to talk about rolling out ChatGPT for my team", source);

  if (variant === "inline") {
    return (
      <aside className="not-prose my-8 rounded-2xl border border-primary/20 bg-primary/5 p-5 sm:p-6">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
          {t(locale, "OpenAI Select Partner in Malaysia and Singapore")}
        </p>
        <p className="mt-2 text-base leading-relaxed text-neutral-emphasis">
          {t(
            locale,
            "Anchor Sprint is an OpenAI Select Partner. We help companies in Malaysia and Singapore roll out ChatGPT and set up agents safely, with HRD Corp claimable training for your team.",
          )}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm font-semibold">
          <Link href={partnerHref} className="inline-flex items-center gap-1 text-primary hover:underline">
            {t(locale, "Talk to us about ChatGPT")} <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
          <WhatsAppCta message={wa} location={`${location}_inline`} label={t(locale, "WhatsApp us")} />
        </div>
      </aside>
    );
  }

  return (
    <section className="not-prose my-12 overflow-hidden rounded-3xl bg-hero-background px-6 py-10 md:px-12 md:py-14">
      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary-contrast/60">
        {t(locale, "OpenAI Select Partner in Malaysia and Singapore")}
      </p>
      <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-primary-contrast md:text-3xl">
        {t(locale, title)}
      </h3>
      <p className="mt-4 max-w-2xl text-primary-contrast/75">
        {t(
          locale,
          "Anchor Sprint is an OpenAI Select Partner. We help companies in Malaysia and Singapore roll out ChatGPT, set up agents safely with approval rules and a review routine, and train the people who use them.",
        )}
      </p>
      <ul className="mt-6 space-y-2">
        {POINTS.map((p) => (
          <li key={p} className="flex items-start gap-3 text-sm text-primary-contrast/85">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            {t(locale, p)}
          </li>
        ))}
      </ul>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <Link
          href={partnerHref}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
        >
          {t(locale, "Talk to us about ChatGPT")}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
        <WhatsAppCta message={wa} location={`${location}_cta`} label={t(locale, "WhatsApp us")} />
      </div>
    </section>
  );
}
